import { Suspense, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';
import { MarkdownDisplay } from '../components/shared/MarkdownDisplay';
import { KnowledgeStatusBadge } from '../components/knowledge/KnowledgeStatusBadge';
import { DocumentKnowledgeBases } from '../components/knowledge/DocumentKnowledgeBases';
import {
  LazyCsvViewer,
  LazyDocxViewer,
  LazyPdfViewer,
  LazyXlsxViewer,
} from '../components/viewers';
import {
  useDeleteKnowledgeDocument,
  useKnowledgeDocument,
  useKnowledgeDocumentFile,
  useKnowledgeMembershipIndex,
} from '../hooks/useKnowledge';
import { useFlag } from '../hooks/useFeatureFlags';
import { knowledgeDocumentsApi } from '../services/api';
import { saveBlobAsFile } from '../utils/downloadFile';
import type { KnowledgeDocument } from '../types';

/**
 * One knowledge document, rendered in the browser (task 1081).
 *
 * The header carries what the Documents table row carries — status, source,
 * size, knowledge-base membership, Delete — plus Download. The body renders the
 * original file, fetched through the authenticated API client as bytes and
 * handed to a viewer chosen by the record's `extension` and `sourceType`.
 *
 * A document is viewable in any status: the file exists from the moment of
 * upload, and Failed is exactly when a User most wants to look at it.
 */

/** Which viewer renders a document. Exported for the dispatch test. */
export type KnowledgeDocumentViewerKind = 'pdf' | 'docx' | 'xlsx' | 'csv' | 'md' | 'text' | 'unsupported';

export function viewerKindFor(
  document: Pick<KnowledgeDocument, 'extension' | 'sourceType'>
): KnowledgeDocumentViewerKind {
  switch (document.extension) {
    case 'pdf':
    case 'docx':
    case 'xlsx':
    case 'csv':
    case 'md':
      return document.extension;
    case 'txt':
      return 'text';
    default:
      return document.sourceType === 'text' ? 'text' : 'unsupported';
  }
}

function formatSize(bytes: number | null): string | null {
  if (bytes === null || bytes === undefined) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function sourceLabel(document: KnowledgeDocument): string {
  if (document.sourceType === 'text') return 'Pasted text';
  return document.extension ? document.extension.toUpperCase() : 'File';
}

/** The name a downloaded file is saved under. */
function downloadName(document: KnowledgeDocument): string {
  if (document.fileName) return document.fileName;
  return document.sourceType === 'text' ? `${document.name}.txt` : document.name;
}

function ViewerSpinner({ label }: { label: string }) {
  return (
    <div
      data-testid="knowledge-document-viewer-loading"
      className="flex h-full min-h-[16rem] flex-col items-center justify-center gap-3 text-gray-500 dark:text-gray-400"
    >
      <ArrowPathIcon className="h-8 w-8 animate-spin" aria-hidden="true" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

interface DocumentViewerBodyProps {
  kind: KnowledgeDocumentViewerKind;
  data: ArrayBuffer;
}

/** Dispatches the bytes to the viewer for `kind`. */
export function DocumentViewerBody({ kind, data }: DocumentViewerBodyProps) {
  const text = useMemo(
    () => (kind === 'csv' || kind === 'md' || kind === 'text' ? new TextDecoder().decode(data) : ''),
    [kind, data]
  );

  switch (kind) {
    case 'pdf':
      return <LazyPdfViewer data={data} />;
    case 'docx':
      return <LazyDocxViewer data={data} />;
    case 'xlsx':
      return <LazyXlsxViewer data={data} />;
    case 'csv':
      return <LazyCsvViewer content={text} />;
    case 'md':
      return (
        <div className="h-full overflow-auto p-4" data-testid="knowledge-document-markdown">
          <MarkdownDisplay content={text} bare />
        </div>
      );
    case 'text':
      return (
        <pre
          data-testid="knowledge-document-text"
          className="h-full overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-sm text-gray-800 dark:text-gray-200"
        >
          {text}
        </pre>
      );
    default:
      return (
        <div className="flex h-full min-h-[16rem] items-center justify-center p-6 text-sm text-gray-500 dark:text-gray-400">
          This file type has no in-browser preview. Download it to open it.
        </div>
      );
  }
}

export default function KnowledgeDocumentPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const viewEnabled = useFlag('innovate.fe.knowledge.documents.view');

  const { data: document, isLoading, error } = useKnowledgeDocument(id);
  // The file is only fetched when the viewer body is on: with the flag off
  // the page is header and actions only.
  const file = useKnowledgeDocumentFile(viewEnabled ? id : '');
  const { bases, index: membershipIndex } = useKnowledgeMembershipIndex();
  const deleteDocument = useDeleteKnowledgeDocument();

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    if (!document) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await knowledgeDocumentsApi.getFile(document.id, { download: true });
      saveBlobAsFile(blob, downloadName(document));
    } catch (downloadFailure) {
      console.error('Failed to download the document:', downloadFailure);
      setDownloadError('The file could not be downloaded. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading document...</div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">
          Error loading document. Please try again.
        </div>
      </div>
    );
  }

  const size = formatSize(document.sizeBytes);
  const kind = viewerKindFor(document);

  return (
    <div data-testid="knowledge-document-page">
      <Link
        to="/knowledge-documents"
        className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      >
        <ArrowLeftIcon className="mr-1 h-4 w-4" aria-hidden="true" />
        Documents
      </Link>

      <div className="mt-3 md:flex md:items-start md:justify-between">
        <div className="min-w-0 flex-1">
          <h1
            data-testid="knowledge-document-title"
            className="text-2xl font-bold text-gray-900 dark:text-white break-words"
          >
            {document.name}
          </h1>
          {document.description && (
            <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {document.description}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
            <KnowledgeStatusBadge document={document} />
            <span data-testid="knowledge-document-meta">
              {sourceLabel(document)}
              {size && ` · ${size}`}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400">Knowledge bases:</span>
            <DocumentKnowledgeBases
              documentId={document.id}
              memberships={membershipIndex.get(document.id) ?? []}
              allBases={bases}
            />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 md:ml-4 md:mt-0">
          <button
            type="button"
            data-testid="knowledge-document-download"
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="-ml-1 mr-2 h-4 w-4" aria-hidden="true" />
            {downloading ? 'Downloading…' : 'Download'}
          </button>
          <DeleteResourceControl
            flag="innovate.fe.knowledge.documents.delete"
            entityLabel="document"
            name={document.name}
            id={document.id}
            variant="button"
            testId="knowledge-document-delete"
            body={
              <p>
                This removes the document everywhere: it disappears from every knowledge base it
                belongs to, and agents stop being able to draw on it. To take it out of one
                grouping instead, remove it from that knowledge base.
              </p>
            }
            onDelete={() => deleteDocument.mutateAsync(document.id)}
            onDeleted={() => navigate('/knowledge-documents')}
          />
        </div>
      </div>

      {downloadError && (
        <p data-testid="knowledge-document-download-error" className="mt-3 text-sm text-red-600 dark:text-red-400">
          {downloadError}
        </p>
      )}

      {viewEnabled && (
        <div
          data-testid="knowledge-document-viewer"
          data-viewer={kind}
          className="mt-6 h-[75vh] min-h-[24rem] overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm"
        >
          {file.isLoading ? (
            <ViewerSpinner label="Loading document…" />
          ) : file.error || !file.data ? (
            <div
              data-testid="knowledge-document-viewer-error"
              className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center"
            >
              <ExclamationTriangleIcon className="h-10 w-10 text-red-400 dark:text-red-500" aria-hidden="true" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                This document could not be displayed.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                The stored file could not be read right now. You can still try downloading it.
              </p>
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="-ml-1 mr-2 h-4 w-4" aria-hidden="true" />
                Download
              </button>
            </div>
          ) : (
            <Suspense fallback={<ViewerSpinner label="Opening viewer…" />}>
              <DocumentViewerBody kind={kind} data={file.data} />
            </Suspense>
          )}
        </div>
      )}
    </div>
  );
}
